import React, { useState, useEffect, useCallback } from "react";
import {
  Newspaper, Plus, Trash2, Pencil, Loader2, RefreshCw, Star, StarOff, X, Save, Upload
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";
const SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL || "http://localhost:3001";

const EMPTY_FORM = { title: "", content: "", image_url: "", is_featured: false };

export default function NewsManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploadingImage, setUploadingImage] = useState(false);
  const token = localStorage.getItem("ka_admin_token");

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/news`);
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load news posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openNew = () => {
    setEditingPost(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title || "",
      content: post.content || "",
      image_url: post.image_url || "",
      is_featured: !!post.is_featured,
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    const fd = new FormData();
    fd.append("image", file);
    fd.append("title", form.title || file.name);
    try {
      const res = await fetch(`${API_URL}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setForm(f => ({ ...f, image_url: `${SERVER_URL}${data.url}` }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      const url = editingPost ? `${API_URL}/news/${editingPost.id}` : `${API_URL}/news`;
      const method = editingPost ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(editingPost ? "Post updated" : "Post published");
      setDialogOpen(false);
      fetchPosts();
    } catch {
      toast.error("Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!confirm(`Delete "${post.title}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/news/${post.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success("Post deleted");
      setPosts(prev => prev.filter(p => p.id !== post.id));
    } catch {
      toast.error("Failed to delete post");
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold flex items-center gap-2 text-white">
          <Newspaper className="w-5 h-5 text-[#8B0000]" />
          News & Updates
        </h3>
        <div className="flex gap-2">
          <Button onClick={fetchPosts} disabled={loading} className="bg-transparent border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 hover:border-white/20">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={openNew} className="bg-[#8B0000] hover:bg-[#B22222] text-white">
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </div>
      </div>

      {/* Post list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#8B0000] animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-20 bg-white/[0.02] border border-dashed border-[#8B0000]/20 rounded-lg">
          <Newspaper className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">No news posts yet. Create your first one.</p>
          <Button onClick={openNew} className="mt-4 bg-[#8B0000] hover:bg-[#B22222] text-white">
            <Plus className="w-4 h-4 mr-2" /> New Post
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <Card key={post.id} className="bg-[#1a1a1a]/80 border-[#8B0000]/20 hover:border-[#8B0000]/40 transition-colors">
              <CardContent className="p-0">
                <div className="flex gap-4 p-4">
                  {/* Thumbnail */}
                  <div className="w-24 h-20 rounded-lg overflow-hidden bg-black/40 shrink-0">
                    {post.image_url ? (
                      <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="w-6 h-6 text-gray-600" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="font-semibold text-white">{post.title}</h4>
                      {post.is_featured === 1 && (
                        <Badge className="bg-[#8B0000]/20 text-[#ff8080] border border-[#8B0000]/40 text-xs shrink-0">
                          <Star className="w-3 h-3 mr-1 fill-current" /> Featured
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-2">{post.content}</p>
                    <p className="text-xs text-gray-600 mt-1">{formatDate(post.created_at)}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => openEdit(post)}
                      className="text-gray-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => handleDelete(post)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#111111] border-[#8B0000]/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-[#8B0000]" />
              {editingPost ? "Edit Post" : "New Post"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm text-gray-400">Title *</label>
              <Input
                className="bg-white/5 border-[#8B0000]/30 text-white"
                placeholder="Post title"
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-gray-400">Content *</label>
              <Textarea
                className="bg-white/5 border-[#8B0000]/30 text-white min-h-[160px]"
                placeholder="Write your post content..."
                value={form.content}
                onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-400">Image</label>
              <div className="flex gap-2">
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#8B0000]/30 bg-white/5 text-sm text-gray-300 hover:text-white hover:bg-white/10 cursor-pointer transition-colors">
                  {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploadingImage ? "Uploading..." : "Upload Image"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                    disabled={uploadingImage}
                  />
                </label>
                <span className="text-gray-600 text-sm self-center">or paste URL:</span>
              </div>
              <Input
                className="bg-white/5 border-[#8B0000]/30 text-white"
                placeholder="https://..."
                value={form.image_url}
                onChange={(e) => setForm(f => ({ ...f, image_url: e.target.value }))}
              />
              {form.image_url && (
                <div className="h-40 rounded-lg overflow-hidden border border-[#8B0000]/20">
                  <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = "none"} />
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, is_featured: !f.is_featured }))}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${
                form.is_featured
                  ? "bg-[#8B0000]/20 border-[#8B0000]/60 text-[#ff8080]"
                  : "bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/20"
              }`}
            >
              {form.is_featured
                ? <Star className="w-4 h-4 fill-current" />
                : <StarOff className="w-4 h-4" />}
              {form.is_featured ? "Featured post" : "Mark as featured"}
            </button>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#8B0000] hover:bg-[#B22222] text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingPost ? "Save Changes" : "Publish Post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
